import type {
  OnboardingTourProgress,
  OnboardingTourProgressResponse,
  UpdateOnboardingTourProgressRequest,
} from "@playbookai/types";
import { getCsrfToken } from "../auth/session-api.js";

/**
 * Onboarding-tour progress, owned by Auth (ADR-0021) and reached through the
 * BFF like every other Auth route. There is no tenant or user on the wire: the
 * session decides both.
 */

/* The controller's own path, verbatim — `@Controller("api/auth/me/onboarding-tours")`,
   and what `docs/api/auth.yaml` and the route inventory both record. There is no
   `/v1` segment on Auth's user-scoped routes (compare `/api/auth/directory/search`
   and `/api/auth/admin/...`); an earlier cut had one here, which would have 404'd
   every read and write while every unit test kept passing, because nothing in
   this app resolves the path against the service. */
const BASE = "/api/auth/me/onboarding-tours";

export async function listTourProgress(
  signal?: AbortSignal,
): Promise<OnboardingTourProgressResponse> {
  const response = await fetch(BASE, {
    method: "GET",
    credentials: "include",
    headers: { accept: "application/json" },
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    // Deliberately a THROW rather than an empty list. An empty list is
    // "this person has no records", which is eligibility — so returning it on
    // a failed read would start every tour on every failure, and restart them
    // on every retry.
    throw new Error(`Onboarding progress read failed (${response.status}).`);
  }
  const body = (await response.json()) as OnboardingTourProgressResponse;
  return body;
}

export async function putTourProgress(
  request: UpdateOnboardingTourProgressRequest,
): Promise<OnboardingTourProgress> {
  const csrf = getCsrfToken();
  const response = await fetch(BASE, {
    method: "PUT",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      ...(csrf ? { "x-csrf-token": csrf } : {}),
    },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error(`Onboarding progress write failed (${response.status}).`);
  }
  return (await response.json()) as OnboardingTourProgress;
}

export async function resetTours(
  tourId: UpdateOnboardingTourProgressRequest["tourId"] | "all",
): Promise<{ resetCount: number }> {
  const csrf = getCsrfToken();
  const response = await fetch("/api/auth/admin/onboarding-tours/reset", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(csrf ? { "x-csrf-token": csrf } : {}),
    },
    body: JSON.stringify({ tourId }),
  });
  if (!response.ok)
    throw new Error(
      `Could not reset onboarding tours (${response.status}). Please try again.`,
    );
  return response.json() as Promise<{ resetCount: number }>;
}
